import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, FileText, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const PROPERTY_TYPES = ["Appartamento", "Villa", "Attico", "Loft", "Ufficio", "Negozio", "Altro"];
const PHASES = ["Sto valutando l'acquisto", "Ho appena acquistato", "Da ristrutturare", "Ristrutturazione in corso", "Voglio solo rinnovare gli interni"];

export default function LeadPdfModal({ slug, serviceTitle, sample, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", property_type: "",
    area_sqm: "", project_phase: "", privacy_consent: false, newsletter_consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.type === "checkbox" ? e.target.checked : e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) { toast.error("Il consenso alla Privacy è obbligatorio"); return; }
    setBusy(true);
    try {
      await api.post("/lead-pdf", { ...form, service_slug: slug });
      setDone(true);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore, riprova");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-lenis-prevent data-testid="lead-pdf-modal" onClick={onClose}>
      <div className="relative bg-bone w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 z-10 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-gold transition-colors" data-testid="lead-pdf-close" aria-label="Chiudi">
          <X size={18} />
        </button>

        {done ? (
          <div className="p-8 md:p-10 text-center" data-testid="lead-pdf-success">
            <div className="mx-auto w-12 h-12 border border-gold text-gold flex items-center justify-center"><Check size={22} /></div>
            <h3 className="font-serif text-3xl mt-6">Controlla la tua email.</h3>
            <p className="text-taupe mt-4 text-sm leading-relaxed">
              Ti abbiamo appena inviato l'estratto del progetto di <strong>{serviceTitle}</strong> in PDF.
              Se non lo vedi, controlla la cartella spam.
            </p>
            <Link to="/prenota" className="btn-ink mt-8 inline-flex w-full justify-center min-h-[48px]" data-testid="lead-pdf-cta-call">
              Prenota una chiamata conoscitiva
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 md:p-10 space-y-5" data-testid="lead-pdf-form">
            <div>
              <div className="eyebrow">{serviceTitle}</div>
              <h3 className="font-serif text-3xl mt-2">{sample?.title || "Ricevi un estratto del progetto"}</h3>
              {sample?.description && <p className="text-taupe text-sm mt-2">{sample.description}</p>}
              <p className="text-taupe text-sm mt-3">Lascia i tuoi dati: ti invieremo il PDF via email.</p>
            </div>

            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Nome e Cognome *</Label>
              <Input required value={form.name} onChange={set("name")} className="rounded-none mt-2" data-testid="lead-name" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Email *</Label>
              <Input type="email" required value={form.email} onChange={set("email")} className="rounded-none mt-2" data-testid="lead-email" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Telefono (facoltativo)</Label>
              <Input value={form.phone} onChange={set("phone")} className="rounded-none mt-2" data-testid="lead-phone" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs tracking-[0.14em] uppercase">Città dell'immobile</Label>
                <Input value={form.city} onChange={set("city")} className="rounded-none mt-2" data-testid="lead-city" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.14em] uppercase">Metratura indicativa</Label>
                <Input value={form.area_sqm} onChange={set("area_sqm")} placeholder="es. 90 mq" className="rounded-none mt-2" data-testid="lead-area" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Tipologia di immobile</Label>
              <Select value={form.property_type} onValueChange={set("property_type")}>
                <SelectTrigger className="rounded-none mt-2" data-testid="lead-property-type"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Servizio interessato</Label>
              <Input value={serviceTitle} disabled className="rounded-none mt-2 disabled:opacity-70" data-testid="lead-service" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Fase del progetto</Label>
              <Select value={form.project_phase} onValueChange={set("project_phase")}>
                <SelectTrigger className="rounded-none mt-2" data-testid="lead-phase"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {PHASES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer">
              <input type="checkbox" checked={form.privacy_consent} onChange={set("privacy_consent")} className="mt-1" data-testid="lead-privacy" />
              <span>Ho letto la <Link to="/privacy" className="underline hover:text-gold">Privacy Policy</Link> e acconsento al trattamento dei dati per ricevere il PDF. *</span>
            </label>
            <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer">
              <input type="checkbox" checked={form.newsletter_consent} onChange={set("newsletter_consent")} className="mt-1" data-testid="lead-newsletter" />
              <span>Voglio ricevere anche il Diario mensile (facoltativo, cancellazione con un click).</span>
            </label>

            <button type="submit" disabled={busy || !form.privacy_consent} className="btn-ink w-full min-h-[48px] disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="lead-submit">
              <FileText size={15} /> {busy ? "Invio…" : "Ricevi il PDF via email"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
